import React from 'react';
import {Input} from 'antd';
import {Controller, useFormContext} from 'react-hook-form';
import ImageUploadField from '../forms/ImageUploadField';
import FormModal from './FormModal';
import FormItem from '../forms/utils/FormItem';
import IncludedCategories from '../forms/IncludedeCategories';
import ExcludedCategories from '../forms/ExcludedCategories';
import Select from '../forms/Select';
import {useQuery} from '@apollo/client';
import {GET_CONTENT_CATEGORIES} from '../../utils/api/trafficDriver';
import {TeaserFormat} from '../../utils/CurrentUserContext';
import SingleLineField from '../forms/SingleLineField';
import {optionalHttpsUrlRule, teaserHeadlineWarning, TEASER_HEADLINE_TOOLTIP} from '../../utils/validators';
import VideoInput from '../forms/utils/VideoInput';

interface TrafficDriverModalProps {
  open: boolean;
  editMode: boolean;
  defaultValues?: any;
  websites: any[];
  allowedTeaserFormats?: TeaserFormat[];
  allowTeaserRibbon?: boolean;
  allowTeaserVideos?: boolean;
  campaignType?: string;
  onCancel: () => void;
  onOk: (trafficDriver: any) => void;
}

const TrafficDriverModalContent: React.FC<{
  websites: any[];
  allowTeaserVideos: boolean;
  allowTeaserRibbon: boolean;
  allowedTeaserFormats?: TeaserFormat[];
  campaignType?: string;
}> = ({ websites, allowTeaserRibbon, allowTeaserVideos, allowedTeaserFormats = [], campaignType }) => {
  const { watch } = useFormContext();
  const selectedFormat = watch('format');

  const showFormatDropdown = allowedTeaserFormats.length > 1;
  const selectedFormatData = allowedTeaserFormats.length > 0
    ? (allowedTeaserFormats.find(f => f.type === selectedFormat) || allowedTeaserFormats[0])
    : undefined;

  const showCustomLandingPage = campaignType === 'IN_HOUSE_NATIVE_DISPLAY' || campaignType === 'MARKETPLACE_NATIVE_DISPLAY' || campaignType === 'CONTENT_RECIRCULATION';

  return (
    <>
      <FormItem top label="Headline" tooltip={TEASER_HEADLINE_TOOLTIP}>
        <Controller
          name="headline"
          rules={{ required: true }}
          render={({ field }) => {
            const warning = teaserHeadlineWarning(field.value)
            return (
              <>
                <Input.TextArea {...field} showCount maxLength={65} />
                {/* mt-7 clears antd's character counter, which is absolutely positioned at bottom:-22px. */}
                {warning && <div className="text-yellow-600 text-sm mt-7">{warning}</div>}
              </>
            )
          }}
        />
      </FormItem>
      {allowTeaserRibbon && (
        <SingleLineField
        label="Ribbon"
        htmlfor="ribbon"
        />
      )}
      {showCustomLandingPage && (
        <SingleLineField
          label="Custom Landing Page"
          htmlfor="destinationUrl"
          required={false}
          rules={{ validate: { format: optionalHttpsUrlRule } }}
        />
      )}

      {showFormatDropdown && (
        <FormItem label="Format">
          <Controller
            name="format"
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <Select
                className="w-full"
                placeholder="Select format"
                options={allowedTeaserFormats.map(({ type, displayName }) => ({ label: displayName, value: type }))}
                value={value}
                onChange={onChange}
              />
            )}
          />
        </FormItem>
      )}

      {/* <span className="flex mt-4 mb-2 font-bold">Targeting Type</span>
      <IncludedCategories show={true} categories={categories} />
      <ExcludedCategories show={true} categories={categories} /> */}

      <ImageUploadField
        label="Image"
        required
        name="imageId"
        minImageSize={selectedFormatData?.minImageSize}
        recommendedAspectRatio={selectedFormatData?.recommendedAspectRatio}
      />
      {allowTeaserVideos && (
        <FormItem label='Video'>
          <VideoInput name="videoId" />
        </FormItem>
      )}

      <FormItem label="Websites">
        <Controller name='websiteIds'
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <Select
              className="w-full"
              placeholder="Select websites"
              mode="multiple"
              options={websites.map(({ _id, name }) => ({ label: name, value: _id }))}
              allowSelectAll
              value={value}
              onChange={onChange}
              maxTagCount="responsive"
              optionFilterProp="label"
            />
          )}
        />
      </FormItem>
    </>
  );
};

const TrafficDriverModal: React.FC<TrafficDriverModalProps> = ({websites, editMode, allowTeaserRibbon = false, allowTeaserVideos = false, allowedTeaserFormats = [], campaignType, ...props }) => {
  return (
    <FormModal
        title={`${editMode ? "Edit" : "Add"} Teaser`}
        okText={editMode ? "Save" : "Add"}
        {...props}
      >
        <TrafficDriverModalContent
          websites={websites}
          allowedTeaserFormats={allowedTeaserFormats}
          allowTeaserRibbon={allowTeaserRibbon}
          allowTeaserVideos={allowTeaserVideos}
          campaignType={campaignType}
        />
      </FormModal>
  );
};

export default TrafficDriverModal;

